import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardService } from '../services/api';
import { DashboardStats } from '../types';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await dashboardService.getStats();
      setStats(data);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-32 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
          <p className="text-sm text-gray-600 dark:text-slate-300">Overview of your study abroad program prospects</p>
        </div>

        {/* Pipeline Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-bold">{stats?.total_prospects || 0}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-300">Total Prospects</p>
              </div>
              <div className="bg-slate-900/10 dark:bg-white/10 rounded-full p-3">
                <svg className="w-6 h-6 text-slate-700 dark:text-slate-100" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          
          {stats?.stage_counts && Object.entries(stats.stage_counts).map(([stageName, count]) => (
            <div key={stageName} className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-6 transition duration-200 transform hover:-translate-y-1">
              <div className="text-center">
                <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">{count}</h3>
                <p className="text-slate-600 dark:text-slate-300 font-medium">{stageName}</p>
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200 border border-emerald-200/60 dark:border-emerald-800/70">
                    {stats?.total_prospects && stats.total_prospects > 0 ? ((count / stats.total_prospects) * 100).toFixed(1) : 0}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div id="recent-prospects" className="lg:col-span-2 bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Recent Prospects</h3>
          </div>
          <div className="p-6">
            {stats?.recent_prospects && stats.recent_prospects.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-gray-200">
                    <tr>
                      <th className="text-left py-3 px-2 text-sm font-semibold text-gray-900">Name</th>
                      <th className="text-left py-3 px-2 text-sm font-semibold text-gray-900">Email</th>
                      <th className="text-left py-3 px-2 text-sm font-semibold text-gray-900">Stage</th>
                      <th className="text-left py-3 px-2 text-sm font-semibold text-gray-900">Added</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {stats.recent_prospects.map((prospect) => (
                      <tr key={prospect.id} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition duration-150">
                        <td className="py-4 px-2">
                          <Link
                            to={`/admin/prospects/${prospect.id}`}
                          className="text-emerald-600 hover:text-emerald-700 font-semibold transition duration-200"
                          >
                            {prospect.first_name} {prospect.last_name}
                          </Link>
                        </td>
                      <td className="py-4 px-2 text-slate-600 dark:text-slate-300">{prospect.email || '-'}</td>
                        <td className="py-4 px-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200 border border-emerald-200/60 dark:border-emerald-800/70">
                            {prospect.current_stage
                              ? prospect.current_stage.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
                              : '—'}
                          </span>
                        </td>
                      <td className="py-4 px-2 text-slate-500 dark:text-slate-400 text-sm">
                          {prospect.created_at ? new Date(prospect.created_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <p className="text-slate-600 dark:text-slate-300">No prospects yet. Start by capturing your first visitor!</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions and Pipeline Guide */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Quick Actions</h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 w-full px-4 py-3"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add New Visitor
                </Link>
                <Link
                  to="/admin/prospects"
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 w-full px-4 py-3"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                  View All Prospects
                </Link>
                <Link
                  to="/admin/scholarships-management"
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 w-full px-4 py-3"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Manage Scholarships
                </Link>
                <Link
                  to="/admin/analytics"
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 hover:border-blue-700 w-full px-4 py-3"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 3a1 1 0 011-1h8a1 1 0 011 1v14a1 1 0 11-2 0v-1H7v1a1 1 0 11-2 0V3zm2 12h6v-2H7v2zm0-4h6V7H7v4z" clipRule="evenodd" />
                  </svg>
                  View Analytics
                </Link>
              </div>
            </div>
          </div>

          {/* Pipeline Stages */}
          <div id="application-stages" className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Application Stages</h3>
            </div>
            <div className="p-6">
              <ol className="space-y-3">
                {stats?.pipeline_stages && stats.pipeline_stages.map(([code, name], index) => (
                  <li key={code} className="flex items-center text-sm text-gray-700">
                    <span className="flex-shrink-0 w-6 h-6 bg-slate-100 text-slate-700 rounded-full flex items-center justify-center text-xs font-semibold mr-3">
                      {index + 1}
                    </span>
                    <div>
                      <span className="font-medium">{name}</span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
